import { GenerateSignedUrlRequestConfig, useStorytellClient } from "@storytell/client";
import type { AssetId } from "@storytell/types";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbBookmark, TbCheck, TbDownload, TbFileText, TbHash, TbLoader2, TbMapPin } from "solid-icons/tb";
import { type Component, Show, createMemo, createSignal } from "solid-js";
import type { KBSearchResultsCollection } from "~/utils/citationResolver";
import { resolveKBCitation, resolveKBCitationAsset } from "~/utils/citationResolver";
import { parseKBCitation } from "~/utils/citationUtils";
import { extractMarkdownText } from "~/utils/textUtils";
import { useWire } from "~/wire";

export interface KBCitationRendererProps {
  node: { attrs: { value: string } };
  selected?: boolean;
  kbSearchResults?: KBSearchResultsCollection;
  searchQuery?: string;
}

/**
 * Renders KB search citations like <cite>asset_abc123_cxp_24</cite> as clickable links
 * with rich tooltips showing chunk content, page numbers, and relevance scores
 */
export const KBCitationRenderer: Component<KBCitationRendererProps> = (props) => {
  const wire = useWire();
  const client = useStorytellClient();

  const parsedCitation = createMemo(() => {
    return parseKBCitation(props.node.attrs.value);
  });

  const chunkData = createMemo(() => {
    return resolveKBCitation(props.node.attrs.value, props.kbSearchResults);
  });

  const assetData = createMemo(() => {
    return resolveKBCitationAsset(props.node.attrs.value, props.kbSearchResults);
  });

  const [isDownloading, setIsDownloading] = createSignal(false);
  const [downloadSuccess, setDownloadSuccess] = createSignal(false);

  const downloadAsset = async (assetId: AssetId, filename: string) => {
    setIsDownloading(true);
    try {
      const requestConfig = GenerateSignedUrlRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(requestConfig, { id: assetId }, undefined);

      if (response.isErr()) {
        console.error("Failed to get signed URL:", response.error);
        setIsDownloading(false);
        return;
      }

      const fileResponse = await fetch(response.value.url);
      const blob = await fileResponse.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);

      setIsDownloading(false);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 1500);
    } catch (err) {
      console.error("Failed to download asset:", err);
      setIsDownloading(false);
    }
  };

  const handleClick = () => {
    const data = chunkData();
    const parsed = parsedCitation();
    if (!data || !parsed || parsed.position === undefined) return;

    downloadAsset(data.asset.assetId as AssetId, data.asset.filename || data.asset.title || "download");
  };

  const handleFallbackClick = () => {
    const asset = assetData();
    const parsed = parsedCitation();
    if (!asset || !parsed || parsed.position === undefined) return;

    downloadAsset(asset.assetId as AssetId, asset.filename || asset.title || "download");
  };

  const relevanceLabel = (score: number) => {
    if (score < 0.71) return { label: "High", color: "text-green-600 dark:text-green-400" };
    if (score < 0.76) return { label: "Medium", color: "text-yellow-600 dark:text-yellow-400" };
    return { label: "Low", color: "text-gray-600 dark:text-gray-400" };
  };

  const highlightQueryTerms = (content: string, searchQuery?: string) => {
    if (!searchQuery) return content;

    const terms = searchQuery
      .toLowerCase()
      .split(/\s+/)
      .filter((term) => term.length > 2);

    if (terms.length === 0) return content;

    let highlighted = content;
    for (const term of terms) {
      const regex = new RegExp(`(${term})`, "gi");
      highlighted = highlighted.replace(regex, "**$1**");
    }

    return highlighted;
  };

  return (
    <Show
      when={chunkData()}
      fallback={
        <Show
          when={assetData() && parsedCitation()?.position !== undefined ? assetData() : null}
          fallback={<span class="text-blue-600 dark:text-blue-400">[{parsedCitation()?.position ?? "?"}]</span>}
        >
          {(asset) => (
            <StTooltip
              placement="top"
              content={
                <div class="px-3 py-3 max-w-md shadow-xl rounded-lg">
                  <div class="mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                    <div class="flex items-center gap-2 mb-1">
                      <TbFileText class="size-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={handleFallbackClick}
                        class="font-semibold text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 truncate cursor-pointer text-left transition-colors"
                      >
                        {asset().filename || asset().title || "Untitled"}
                      </button>
                    </div>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Knowledge Base Citation</p>
                  </div>
                  <div class="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <div class="flex items-center gap-1">
                      <TbHash class="size-3.5" />
                      <span>Citation {parsedCitation()?.position}</span>
                    </div>
                  </div>
                  <div class="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <p class="text-xs flex items-center gap-1.5">
                      <Show
                        when={isDownloading()}
                        fallback={
                          <Show
                            when={downloadSuccess()}
                            fallback={
                              <>
                                <TbDownload class="size-3.5 text-gray-500 dark:text-gray-400" />
                                <span class="text-gray-500 dark:text-gray-400">
                                  Click in the title to download the file
                                </span>
                              </>
                            }
                          >
                            <TbCheck class="size-3.5 text-green-600 dark:text-green-400" />
                            <span class="text-green-600 dark:text-green-400">File downloaded successfully!</span>
                          </Show>
                        }
                      >
                        <TbLoader2 class="size-3.5 animate-spin text-blue-600 dark:text-blue-400" />
                        <span class="text-blue-600 dark:text-blue-400">Downloading the file...</span>
                      </Show>
                    </p>
                  </div>
                </div>
              }
            >
              <StTooltipTrigger
                as="button"
                type="button"
                onClick={handleClick}
                class="inline-flex items-center gap-0.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 transition-all duration-200 cursor-pointer hover:scale-110 active:scale-95"
                data-citation={props.node.attrs.value}
              >
                <Show
                  when={isDownloading()}
                  fallback={
                    <Show when={downloadSuccess()} fallback={<TbBookmark class="inline size-4" />}>
                      <TbCheck class="inline size-4" />
                    </Show>
                  }
                >
                  <TbLoader2 class="inline size-4 animate-spin" />
                </Show>
                <sup class="text-xs font-medium">{parsedCitation()?.position}</sup>
              </StTooltipTrigger>
            </StTooltip>
          )}
        </Show>
      }
    >
      {(data) => (
        <StTooltip
          placement="top"
          content={
            <div class="px-3 py-3 max-w-md shadow-xl rounded-lg">
              {/* Header section */}
              <div class="mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center gap-2 mb-1">
                  <TbFileText class="size-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <button
                    type="button"
                    onClick={handleClick}
                    class="font-semibold text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 truncate cursor-pointer text-left transition-colors"
                  >
                    {data().asset.filename || data().asset.title || "Untitled"}
                  </button>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400">Knowledge Base Citation</p>
              </div>

              {/* Content section */}
              <div class="mb-3">
                <div class="bg-gray-50 dark:bg-gray-900/50 rounded-md p-2.5 border border-gray-200 dark:border-gray-700 max-h-[300px] overflow-y-auto">
                  <p class="text-sm text-gray-900 dark:text-gray-100 leading-relaxed whitespace-pre-wrap">
                    {highlightQueryTerms(extractMarkdownText(data().chunk.content), props.searchQuery)}
                  </p>
                </div>
              </div>

              {/* Metadata footer */}
              <div class="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 mb-3">
                <Show when={data().chunk.location?.pageNumber}>
                  <div class="flex items-center gap-1">
                    <TbMapPin class="size-3.5" />
                    <span>Page {data().chunk.location?.pageNumber}</span>
                  </div>
                </Show>
                <Show when={data().chunk.score !== undefined}>
                  <div class="flex items-center gap-1">
                    <span>Relevance:</span>
                    <span class={relevanceLabel(data().chunk.score).color}>
                      {relevanceLabel(data().chunk.score).label}
                    </span>
                  </div>
                </Show>
                <Show when={parsedCitation()?.position !== undefined}>
                  <div class="flex items-center gap-1">
                    <TbHash class="size-3.5" />
                    <span>Citation {parsedCitation()?.position}</span>
                  </div>
                </Show>
              </div>

              {/* Click hint */}
              <div class="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <p class="text-xs flex items-center gap-1.5">
                  <Show
                    when={isDownloading()}
                    fallback={
                      <Show
                        when={downloadSuccess()}
                        fallback={
                          <>
                            <TbDownload class="size-3.5 text-gray-500 dark:text-gray-400" />
                            <span class="text-gray-500 dark:text-gray-400">
                              Click in the title to download the file
                            </span>
                          </>
                        }
                      >
                        <TbCheck class="size-3.5 text-green-600 dark:text-green-400" />
                        <span class="text-green-600 dark:text-green-400">File downloaded successfully!</span>
                      </Show>
                    }
                  >
                    <TbLoader2 class="size-3.5 animate-spin text-blue-600 dark:text-blue-400" />
                    <span class="text-blue-600 dark:text-blue-400">Downloading the file...</span>
                  </Show>
                </p>
              </div>
            </div>
          }
        >
          <StTooltipTrigger
            as="button"
            type="button"
            onClick={handleClick}
            class="inline-flex items-center gap-0.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 transition-all duration-200 cursor-pointer hover:scale-110 active:scale-95"
            data-citation={props.node.attrs.value}
          >
            <Show
              when={isDownloading()}
              fallback={
                <Show when={downloadSuccess()} fallback={<TbBookmark class="inline size-4" />}>
                  <TbCheck class="inline size-4" />
                </Show>
              }
            >
              <TbLoader2 class="inline size-4 animate-spin" />
            </Show>
            <sup class="text-xs font-medium">{parsedCitation()?.position}</sup>
          </StTooltipTrigger>
        </StTooltip>
      )}
    </Show>
  );
};
